import { Singleton } from 'typescript-ioc/es5'
import aqe from '@pushly/aqe'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiDeleteResponse, IApiResponse } from '@pushly/aqe/lib/interfaces'
import {
    CreateItemGroupPayload,
    ItemGroupResponse,
    UpdateItemGroupPayload,
} from '../features/catalog/item-groups/types'
import { GetItemGroupResponse } from '@pushly/models/lib/structs/catalogs/item-groups'
import { PQueryBuilderField } from '@pushly/models/lib/interfaces'

class ItemGroupCountResponse {
    count: number
}

/**
 * Service for Item Group API interactions.
 * Extends AbstractRequestService for standardized request handling.
 *
 * Target API paths (sailfish CatalogItemGroupController):
 *   POST   /domains/:domainId/catalogs/:catalogId/item-groups
 *   GET    /domains/:domainId/catalogs/:catalogId/item-groups
 *   GET    /domains/:domainId/catalogs/:catalogId/item-groups/:id
 *   PUT    /domains/:domainId/catalogs/:catalogId/item-groups/:id
 *   DELETE /domains/:domainId/catalogs/:catalogId/item-groups/:id
 */
@Singleton
export class ItemGroupsService extends AbstractRequestService {
    private fieldsCache: { domainId: number; fields: PQueryBuilderField[]; expiresAt: number } | null = null
    private static readonly FIELDS_CACHE_TTL_MS = 5 * 60 * 1000

    public constructor() {
        super()
    }

    /**
     * Returns cached fields for the domain, fetching on first call, domain change, or TTL expiry.
     */
    public async getFieldsCached(domainId: number): Promise<PQueryBuilderField[]> {
        const now = Date.now()
        if (this.fieldsCache?.domainId === domainId && now < this.fieldsCache.expiresAt) {
            return this.fieldsCache.fields
        }

        const response = await this.getFields(domainId)
        if (response.ok && response.data) {
            this.fieldsCache = {
                domainId,
                fields: response.data,
                expiresAt: now + ItemGroupsService.FIELDS_CACHE_TTL_MS,
            }
            return response.data
        }

        return []
    }

    public async create(
        domainId: number,
        payload: CreateItemGroupPayload,
        catalogId: string = 'default',
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetItemGroupResponse>> {
        return this.request(HttpMethod.POST, this.getEndpoint(domainId, catalogId), {
            ...options,
            data: payload,
            config: { skipCaseConversion: true },
            responseType: GetItemGroupResponse,
        })
    }

    public async update(
        domainId: number,
        groupId: number,
        data: UpdateItemGroupPayload,
        catalogId: string = 'default',
    ): Promise<IApiResponse<GetItemGroupResponse>> {
        return this.request(HttpMethod.PUT, `${this.getEndpoint(domainId, catalogId)}/${groupId}`, {
            data,
            config: { skipCaseConversion: true },
            responseType: GetItemGroupResponse,
        })
    }

    public async getById(
        domainId: number,
        groupId: number,
        catalogId: string = 'default',
    ): Promise<IApiResponse<GetItemGroupResponse>> {
        return this.request(HttpMethod.GET, `${this.getEndpoint(domainId, catalogId)}/${groupId}`, {
            config: { skipCaseConversion: true },
            responseType: GetItemGroupResponse,
        })
    }

    public async list(
        domainId: number,
        options?: IApiCallOptions,
        catalogId: string = 'default',
    ): Promise<IApiResponse<GetItemGroupResponse[]>> {
        return this.request(HttpMethod.GET, this.getEndpoint(domainId, catalogId), {
            ...options,
            config: { skipCaseConversion: true },
            responseType: [GetItemGroupResponse],
        })
    }

    public async archive(
        domainId: number,
        groupId: number,
        catalogId: string = 'default',
    ): Promise<IApiDeleteResponse> {
        return this.requestDelete(`${this.getEndpoint(domainId, catalogId)}/${groupId}`)
    }

    /**
     * Returns the PQueryBuilderField definitions for the domain's catalog type.
     */
    public async getFields(domainId: number): Promise<IApiResponse<PQueryBuilderField[]>> {
        return this.request(HttpMethod.GET, `${aqe.defaults.publicApiDomain}/domains/${domainId}/item-groups/fields`, {
            config: { skipCaseConversion: true },
        })
    }

    /**
     * Returns the count of catalog items matching the given filters.
     */
    public async count(
        domainId: number,
        filtersJson: Record<string, unknown>,
    ): Promise<IApiResponse<ItemGroupCountResponse>> {
        return this.request(HttpMethod.POST, `${aqe.defaults.publicApiDomain}/domains/${domainId}/item-groups/count`, {
            data: { filters_json: filtersJson },
            config: { skipCaseConversion: true },
            responseType: ItemGroupCountResponse,
        })
    }

    private getEndpoint(domainId: number, catalogId: string): string {
        return `${aqe.defaults.publicApiDomain}/domains/${domainId}/catalogs/${catalogId}/item-groups`
    }
}
